import { Store } from "../../../../../stores/store";
import { useEffect, useRef, useState } from "react";
import { fetchFromGPT, GptMessage } from "../../../../../utils/gptAPIClient";
import { ComponentChatMessageButtonGroup } from "../../../../../types/chat";
import {
    type ChatComponent,
    type ChatMessageComponent,
    createChatMessageComponent,
    isButtonGroup,
    isChatMessage,
    removeButtonGroupFromChat,
    transformToGptMessage,
    updateButtonGroup
} from "@/components/chat/hooks/gpt/utils";
import log from "../../../../../utils/logger";

type GptUiPayload = {
    reply: string | null;
    status: string | null;
};

const STATUS_TAG_REGEX = /\[STATUS\]([\s\S]*?)\[\/STATUS\]/gi;

const normalizeText = (value: unknown): string | null => {
    if (typeof value === "string") {
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : null;
    }

    if (Array.isArray(value)) {
        const parts = value
            .map((entry) => normalizeText(entry))
            .filter((entry): entry is string => Boolean(entry));
        const joined = parts.join("\n").trim();
        return joined.length > 0 ? joined : null;
    }

    if (value && typeof value === "object") {
        const record = value as Record<string, unknown>;
        const keys = ["text", "content", "message", "reply", "status"];
        for (const key of keys) {
            const candidate = normalizeText(record[key]);
            if (candidate) return candidate;
        }
    }

    return null;
};

const splitInlineStatus = (text: string | null): { reply: string | null; status: string | null } => {
    if (!text) return { reply: null, status: null };

    const statuses: string[] = [];
    const reply = text
        .replace(STATUS_TAG_REGEX, (_, statusText: string) => {
            const normalizedStatus = normalizeText(statusText);
            if (normalizedStatus) statuses.push(normalizedStatus);
            return "";
        })
        .trim();

    const status = statuses.join(" ").trim();
    return {
        reply: reply.length > 0 ? reply : null,
        status: status.length > 0 ? status : null,
    };
};

const extractObjectLikeField = (raw: string, field: "reply" | "status"): string | null => {
    const regex = new RegExp(
        `[\"']?${field}[\"']?\\s*:\\s*([\\s\\S]*?)(?=,\\s*[\"']?[A-Za-z0-9_]+[\"']?\\s*:|\\s*\\}$)`,
        "i",
    );
    const match = raw.match(regex);
    if (!match?.[1]) return null;
    return match[1].trim();
};

const parseObjectLikeValue = (rawValue: string | null): unknown => {
    if (!rawValue) return null;
    const value = rawValue.trim();
    if (/^null$/i.test(value)) return null;

    if (
        (value.startsWith('"') && value.endsWith('"')) ||
        (value.startsWith("'") && value.endsWith("'"))
    ) {
        return value.slice(1, -1);
    }

    if (
        (value.startsWith("{") && value.endsWith("}")) ||
        (value.startsWith("[") && value.endsWith("]"))
    ) {
        try {
            return JSON.parse(value);
        } catch {
            return value;
        }
    }

    return value;
};

const parseObjectLikePayload = (raw: string): GptUiPayload | null => {
    if (!raw.trim().startsWith("{") || !raw.trim().endsWith("}")) return null;

    const replyRaw = extractObjectLikeField(raw, "reply");
    const statusRaw = extractObjectLikeField(raw, "status");

    if (replyRaw === null && statusRaw === null) return null;

    return {
        reply: normalizeText(parseObjectLikeValue(replyRaw)),
        status: normalizeText(parseObjectLikeValue(statusRaw)),
    };
};

const parseGptUiPayload = (raw: string): GptUiPayload => {
    try {
        const parsed = JSON.parse(raw);
        if (typeof parsed === "string") {
            const nested = parseGptUiPayload(parsed);
            if (nested.reply || nested.status) return nested;
        }

        if (!parsed || typeof parsed !== "object") {
            const fallbackReply = normalizeText(raw);
            return { reply: fallbackReply, status: null };
        }

        const payload = parsed as Record<string, unknown>;
        const reply =
            normalizeText(payload.reply) ??
            normalizeText(payload.answer) ??
            normalizeText(payload.message) ??
            normalizeText(payload.content) ??
            null;
        const status =
            normalizeText(payload.status) ??
            normalizeText(payload.statusMessage) ??
            normalizeText(payload.state) ??
            null;

        return {
            reply,
            status,
        };
    } catch {
        const objectLikePayload = parseObjectLikePayload(raw);
        if (objectLikePayload) return objectLikePayload;
        return { reply: normalizeText(raw), status: null };
    }
};

type UseGptConversationOptions = {
    onStatusMessage?: (status: string) => void;
};

const useGptConversation = (store: Store, options?: UseGptConversationOptions) => {

    const messages = useRef<GptMessage[]>([]);
    const inputBlocked = useRef<boolean>(false);
    const [inputDisabled, setInputDisabled] = useState<boolean>(true);


    const {
        actualChatThread: chat,
        updateActualChatThread: updateChat,
        updateChatEffect,
    } = store;

    useEffect(() => {
        // When the last component in the chat is a button group and it allows gpt input
        toggleInputEnabled();

        // When the chat updates all written messages (no link, no images etc.) will be transformed into a gpt message backlog for chat context
        transformChatToGptMessages();
    }, [chat]);

    /**
     * Enables the gpt input if the button group allows text input and if the conversation didn't end yet
     *
     * @returns {void}
     */
    const toggleInputEnabled = (): void => {
        console.log("isButtonGroup(chat.at(-1)) ", isButtonGroup(chat.at(-1)))

        if (isButtonGroup(chat.at(-1))) {
            const buttons = chat.at(-1) as ComponentChatMessageButtonGroup;
            console.log("buttons.hasGptInput ", buttons.hasGptInput)
            if (buttons.hasGptInput === true && !inputBlocked.current) {
                setInputDisabled(false);
            } else {
                setInputDisabled(true);
            }
        }
    }

    /**
     * Transforms chat messages to Gpt messages.
     *
     * @returns {void}
     */
    const transformChatToGptMessages = (): void => {
        // Filter messages for written messages
        const chatMessages: ChatMessageComponent[] = chat.filter((item: ChatComponent) => isChatMessage(item)) as ChatMessageComponent[];

        // Transform messages to gpt messages
        messages.current = chatMessages.map((item: ChatMessageComponent) => transformToGptMessage(item));
    }

    /**
     * Sends a text message in the chat interface.
     *
     * @param message - The text message to send.
     * @param enabled - [Optional] Indicates whether the message input is enabled. Defaults to true.
     * @param nextBranch - The next branch of the chat components.
     * @returns A Promise that resolves once the message is sent successfully.
     */
    const sendTextMessage = async (message: string, enabled: boolean = true, nextBranch: ChatComponent[] = []): Promise<void> => {
        if (!enabled) {
            setInputDisabled(true);
            return;
        };

        updateChatEffect("");

        // Disable input
        setInputDisabled(true);

        let newChat = [...chat]

        // Remove button group
        const { chat: chatWithoutButtons, buttonGroup } = removeButtonGroupFromChat(newChat);
        newChat = chatWithoutButtons;
        updateChat(newChat);

        // Add user message
        const userMessageComponent = createChatMessageComponent(message, true);
        newChat = [...newChat, userMessageComponent];
        updateChat(newChat);

        // Add user message to backlog for gpt query
        const newMessages: GptMessage[] = [...messages.current,
        {
            content: message,
            role: "user",
            src: "user",
            isEnd: false
        }];

        log.info("gpt input", newMessages);

        // Send backlog to gpt
        const gptResult = await fetchFromGPT(newMessages);
        const parsedPayload = parseGptUiPayload(gptResult.content);
        const splitFromReply = splitInlineStatus(parsedPayload.reply);
        const reply = splitFromReply.reply;
        const status = [parsedPayload.status, splitFromReply.status]
            .filter((item): item is string => Boolean(item && item.trim().length > 0))
            .join(" ")
            .trim() || null;

        if (status) options?.onStatusMessage?.(status);

        const componentsToAppend: ReturnType<typeof createChatMessageComponent>[] = [];
        if (status) {
            componentsToAppend.push(createChatMessageComponent(`[STATUS]${status}[/STATUS]`, false, 0));
        }
        if (reply) {
            componentsToAppend.push(createChatMessageComponent(reply, false, 2));
        }

        if (componentsToAppend.length > 0) {
            newChat = [...newChat, ...componentsToAppend];
            updateChat(newChat);
        }

        if (!inputBlocked.current && gptResult.isEnd) {
            inputBlocked.current = true;
        }

        // Add last button group back to chat
        if (buttonGroup) {

            if (gptResult.isEnd) {
                const now = new Date();
                let staggeredDelay = now.getTime() + 300

                const nextBranchAi = store.chatAiBranches.find(
                    (el) => el?.id === "39",
                );

                const nextBranchAiMessages =
                    nextBranchAi?.attributes?.chatMessages.map((e, i) => {
                        staggeredDelay += (e?.parameters?.delay ?? 0) * 1000;
                        staggeredDelay += (e?.parameters?.duration ?? 0) * 1000;

                        return {
                            ...e, // clone the template

                            timeToAppear: new Date(staggeredDelay), // staggered animation, first in line is the button message above
                            isUser: false,
                            showSendersName: i === 0,
                        };
                    }) ?? [];

                const endWarningStatusMessage = createChatMessageComponent(
                    "[STATUS]ACHTUNG! Zeitverbindung bricht ab![/STATUS]",
                    false,
                    0,
                );

                newChat = [...newChat, endWarningStatusMessage, ...nextBranchAiMessages];
            } else {
                newChat = [...newChat, updateButtonGroup(buttonGroup)];
            }

            updateChat(newChat);
        }

        if (nextBranch && !gptResult.isEnd) {
            const { chat: _, buttonGroup } = removeButtonGroupFromChat(nextBranch);

            if (buttonGroup) {
                newChat = [...newChat, updateButtonGroup(buttonGroup)];
                updateChat(newChat);
            }
        }

    }

    return {
        inputDisabled,
        sendTextMessage,
    }
}


export default useGptConversation;
